import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';

const RouteMap = ({ pickup, drop }) => {
  console.log('🗺️ RouteMap reçu - Pickup:', pickup, 'Drop:', drop);
  
  const [route, setRoute] = useState(null);
  const [pickupCoords, setPickupCoords] = useState(null);
  const [dropCoords, setDropCoords] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);

  // Fonction pour géocoder une adresse
  const geocodeAddress = async (address) => {
    try {
      console.log('Géocodage de l\'adresse:', address);
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`
      );
      const data = await response.json();
      console.log('Résultat géocodage pour', address, ':', data);
      
      if (data && data.length > 0) {
        const coords = {
          lat: parseFloat(data[0].lat),
          lng: parseFloat(data[0].lon)
        };
        console.log('Coordonnées trouvées:', coords);
        return coords;
      }
      console.log('Aucune coordonnée trouvée pour:', address);
      return null;
    } catch (error) {
      console.error('Erreur de géocodage:', error);
      return null;
    }
  };

  // Fonction pour obtenir l'itinéraire
  const getRoute = async (start, end) => {
    try {
      console.log('Récupération de l\'itinéraire de', start, 'vers', end);
      
      // Essayer d'abord avec OSRM (Open Source Routing Machine)
      try {
        const osrmResponse = await fetch(
          `https://router.project-osrm.org/route/v1/driving/${start.lng},${start.lat};${end.lng},${end.lat}?overview=full&geometries=geojson`
        );
        
        if (osrmResponse.ok) {
          const osrmData = await osrmResponse.json();
          console.log('Réponse OSRM:', osrmData);
          
          if (osrmData.routes && osrmData.routes.length > 0) {
            const coordinates = osrmData.routes[0].geometry.coordinates.map(coord => [coord[1], coord[0]]);
            console.log('Itinéraire trouvé avec OSRM:', coordinates.length, 'points');
            return coordinates;
          }
        }
      } catch (osrmError) {
        console.log('Erreur OSRM:', osrmError);
      }
      
      // Si OSRM échoue, créer une ligne droite simple
      console.log('Création d\'une ligne droite entre les deux points...');
      const straightLine = [
        [start.lat, start.lng],
        [end.lat, end.lng]
      ];
      console.log('Ligne droite créée:', straightLine);
      return straightLine;
      
    } catch (error) {
      console.error('Erreur lors de la récupération de l\'itinéraire:', error);
      // Retourner une ligne droite en cas d'erreur
      return [
        [start.lat, start.lng],
        [end.lat, end.lng]
      ];
    }
  };

  // Initialiser la carte
  const initializeMap = useCallback(() => {
    if (!window.L || !pickupCoords || !dropCoords || !mapRef.current) return;

    // Supprimer complètement la carte existante
    if (mapInstanceRef.current) {
      try {
        mapInstanceRef.current.remove();
      } catch (e) {
        console.log('Erreur lors de la suppression de la carte:', e);
      }
      mapInstanceRef.current = null;
    }

    // Nettoyer le conteneur DOM
    if (mapRef.current) {
      mapRef.current.innerHTML = '';
      mapRef.current._leaflet_id = null;
    }

    // Petite pause pour s'assurer que le nettoyage est terminé
    setTimeout(() => {
      try {
        // Créer une nouvelle carte
        const map = window.L.map(mapRef.current, {
          zoomControl: true,
          attributionControl: true
        }).setView([pickupCoords.lat, pickupCoords.lng], 13);

        // Ajouter les tuiles OpenStreetMap avec style sombre
        window.L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', {
          attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>',
          subdomains: 'abcd',
          maxZoom: 20
        }).addTo(map);

        // Créer des icônes personnalisées pour les marqueurs
        const createCustomIcon = (color, icon) => {
          return window.L.divIcon({
            className: 'custom-marker',
            html: `
              <div style="
                background-color: ${color};
                width: 30px;
                height: 30px;
                border-radius: 50%;
                border: 3px solid white;
                display: flex;
                align-items: center;
                justify-content: center;
                box-shadow: 0 2px 8px rgba(0,0,0,0.4);
                font-size: 14px;
                color: white;
                font-weight: bold;
              ">${icon}</div>
            `,
            iconSize: [30, 30],
            iconAnchor: [15, 15],
            popupAnchor: [0, -15]
          });
        };

        // Ajouter les marqueurs avec icônes personnalisées
        const pickupIcon = createCustomIcon('#4CAF50', '🚀'); // Vert pour le départ
        const pickupMarker = window.L.marker([pickupCoords.lat, pickupCoords.lng], { icon: pickupIcon }).addTo(map);
        const pickupText = typeof pickup === 'object' ? pickup.description : pickup;
        pickupMarker.bindPopup(`
          <div style="color: #333; font-weight: bold;">
            <strong style="color: #4CAF50;">🚀 Départ</strong><br/>
            ${pickupText}
          </div>
        `);

        const dropIcon = createCustomIcon('#FF5722', '🎯'); // Orange-rouge pour l'arrivée
        const dropMarker = window.L.marker([dropCoords.lat, dropCoords.lng], { icon: dropIcon }).addTo(map);
        const dropText = typeof drop === 'object' ? drop.description : drop;
        dropMarker.bindPopup(`
          <div style="color: #333; font-weight: bold;">
            <strong style="color: #FF5722;">🎯 Arrivée</strong><br/>
            ${dropText}
          </div>
        `);

        // Ajouter l'itinéraire si disponible
        if (route && route.length > 0) {
          console.log('Affichage de l\'itinéraire avec', route.length, 'points');
          
          // Couleurs personnalisées pour l'itinéraire
          const isSimpleLine = route.length === 2;
          
          // Ajouter une bordure sombre pour le contraste
          const borderStyle = {
            color: '#000000',
            weight: 8,
            opacity: 0.8,
            dashArray: isSimpleLine ? '15, 15' : null,
            lineCap: 'round',
            lineJoin: 'round'
          };
          window.L.polyline(route, borderStyle).addTo(map);
          
          // Itinéraire principal avec couleurs plus vives et visibles
          const routeStyle = {
            color: isSimpleLine ? '#FFEB3B' : '#00E5FF', // Jaune vif pour ligne droite, cyan électrique pour itinéraire réel
            weight: 6,
            opacity: 1,
            dashArray: isSimpleLine ? '12, 8' : null,
            lineCap: 'round',
            lineJoin: 'round'
          };
          
          const polyline = window.L.polyline(route, routeStyle).addTo(map);
          
          // Ajouter un effet de brillance plus visible
          const glowStyle = {
            color: isSimpleLine ? '#FFF59D' : '#80DEEA',
            weight: 3,
            opacity: 0.9,
            dashArray: isSimpleLine ? '6, 4' : null,
            lineCap: 'round',
            lineJoin: 'round'
          };
          window.L.polyline(route, glowStyle).addTo(map);
          
          map.fitBounds(polyline.getBounds().pad(0.1));
        } else {
          console.log('Aucun itinéraire disponible, ajustement de la vue pour les marqueurs');
          // Ajuster la vue pour inclure les deux marqueurs
          const group = new window.L.featureGroup([pickupMarker, dropMarker]);
          map.fitBounds(group.getBounds().pad(0.1));
        }

        mapInstanceRef.current = map;
      } catch (error) {
        console.error('Erreur lors de l\'initialisation de la carte:', error);
      }
    }, 100);
  }, [pickupCoords, dropCoords, route, pickup, drop]);

  // Charger Leaflet et initialiser la carte
  useEffect(() => {
    const currentMapRef = mapRef.current; // Copier la ref pour le nettoyage
    
    const loadLeaflet = () => {
      return new Promise((resolve) => {
        if (window.L) {
          resolve();
          return;
        }

        // Charger CSS
        const cssLink = document.createElement('link');
        cssLink.rel = 'stylesheet';
        cssLink.href = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.css';
        document.head.appendChild(cssLink);

        // Ajouter des styles personnalisés pour le thème sombre
        const customStyles = document.createElement('style');
        customStyles.textContent = `
          .leaflet-container {
            background-color: #1a1a1a !important;
          }
          .leaflet-control-container .leaflet-control {
            background-color: #000000 !important;
            border: 1px solid #444 !important;
            color: #fff !important;
          }
          .leaflet-control-zoom a {
            background-color: #000000 !important;
            border: 1px solid #444 !important;
            color: #ffffff !important;
            font-weight: bold !important;
            text-decoration: none !important;
          }
          .leaflet-control-zoom a:hover {
            background-color: #333333 !important;
            color: #ffffff !important;
          }
          .leaflet-popup-content-wrapper {
            background-color: #2d2d2d !important;
            color: #fff !important;
            border-radius: 8px !important;
            box-shadow: 0 4px 12px rgba(0,0,0,0.3) !important;
          }
          .leaflet-popup-tip {
            background-color: #2d2d2d !important;
          }
          .custom-marker {
            filter: drop-shadow(0 2px 4px rgba(0,0,0,0.3));
          }
        `;
        document.head.appendChild(customStyles);

        // Charger JS
        const script = document.createElement('script');
        script.src = 'https://unpkg.com/leaflet@1.9.4/dist/leaflet.js';
        script.onload = resolve;
        document.head.appendChild(script);
      });
    };

    loadLeaflet().then(() => {
      if (pickupCoords && dropCoords) {
        setTimeout(initializeMap, 100); // Petit délai pour s'assurer que le DOM est prêt
      }
    });

    return () => {
      // Nettoyage complet de la carte
      if (mapInstanceRef.current) {
        try {
          mapInstanceRef.current.remove();
        } catch (e) {
          console.log('Erreur lors du nettoyage:', e);
        }
        mapInstanceRef.current = null;
      }
      
      // Nettoyer le conteneur DOM
      if (currentMapRef) {
        currentMapRef.innerHTML = '';
        if (currentMapRef._leaflet_id) {
          currentMapRef._leaflet_id = null;
        }
      }
    };
  }, [pickupCoords, dropCoords, route, initializeMap]);

  // Effet pour récupérer les données de route
  useEffect(() => {
    const fetchRouteData = async () => {
      if (!pickup || !drop) {
        setPickupCoords(null);
        setDropCoords(null);
        setRoute(null);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        let pickupResult, dropResult;

        // Vérifier si pickup et drop sont des objets avec coordonnées ou des chaînes
        if (typeof pickup === 'object' && pickup.coords) {
          // Utiliser les coordonnées déjà disponibles
          pickupResult = {
            lat: pickup.coords.lat || pickup.lat,
            lng: pickup.coords.lng || pickup.lon
          };
          console.log('Coordonnées pickup déjà disponibles:', pickupResult);
        } else {
          // Géocoder l'adresse si c'est une chaîne
          pickupResult = await geocodeAddress(pickup);
        }

        if (typeof drop === 'object' && drop.coords) {
          // Utiliser les coordonnées déjà disponibles
          dropResult = {
            lat: drop.coords.lat || drop.lat,
            lng: drop.coords.lng || drop.lon
          };
          console.log('Coordonnées drop déjà disponibles:', dropResult);
        } else {
          // Géocoder l'adresse si c'est une chaîne
          dropResult = await geocodeAddress(drop);
        }

        if (!pickupResult || !dropResult) {
          setError('Impossible de localiser une ou plusieurs adresses');
          return;
        }

        console.log('Coordonnées finales - Pickup:', pickupResult, 'Drop:', dropResult);

        setPickupCoords(pickupResult);
        setDropCoords(dropResult);

        // Obtenir l'itinéraire
        const routeCoords = await getRoute(pickupResult, dropResult);
        // S'assurer qu'on a toujours un itinéraire, même si c'est juste une ligne droite
        setRoute(routeCoords || [
          [pickupResult.lat, pickupResult.lng],
          [dropResult.lat, dropResult.lng]
        ]);
      } catch (err) {
        setError('Erreur lors du chargement de la carte');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchRouteData();
  }, [pickup, drop]);

  if (loading) {
    return (
      <Box 
        sx={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          borderRadius: 2,
          border: '2px solid #444',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress size={40} sx={{ color: '#2196F3' }} />
          <Typography sx={{ mt: 2, color: '#fff', fontWeight: 'bold' }}>
            Chargement de la carte...
          </Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box 
        sx={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          borderRadius: 2,
          border: '2px solid #f44336',
          boxShadow: '0 4px 12px rgba(244,67,54,0.2)'
        }}
      >
        <Typography color="#f44336" sx={{ fontWeight: 'bold' }}>
          ⚠️ {error}
        </Typography>
      </Box>
    );
  }

  if (!pickup || !drop) {
    return (
      <Box 
        sx={{ 
          height: '400px', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          backgroundColor: '#1a1a1a',
          borderRadius: 2,
          border: '2px solid #444',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      >
        <Typography sx={{ color: '#bbb', fontStyle: 'italic' }}>
          📍 Veuillez saisir les adresses de départ et d'arrivée
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ height: '400px', width: '100%', mt: 2, borderRadius: 1, overflow: 'hidden' }}>
      <div 
        ref={mapRef}
        style={{ 
          height: '100%', 
          width: '100%',
          border: '2px solid #444',
          borderRadius: '8px',
          backgroundColor: '#1a1a1a',
          boxShadow: '0 4px 12px rgba(0,0,0,0.3)'
        }}
      />
    </Box>
  );
};

export default RouteMap;
